import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [purchasedCourseIds, setPurchasedCourseIds] = useState([]);
    const [licenseOwner, setLicenseOwner] = useState(false);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("ai4faculty_token");
        if (!token) {
            setUser(null);
            setPurchasedCourseIds([]);
            setLicenseOwner(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            setPurchasedCourseIds(data.purchased_course_ids || []);
            setLicenseOwner(!!data.license_owner);
        } catch {
            localStorage.removeItem("ai4faculty_token");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("ai4faculty_token", data.token);
        setUser(data.user);
        await refresh();
        return data.user;
    }, [refresh]);

    const register = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        localStorage.setItem("ai4faculty_token", data.token);
        setUser(data.user);
        await refresh();
        return data.user;
    }, [refresh]);

    const logout = useCallback(() => {
        localStorage.removeItem("ai4faculty_token");
        setUser(null);
        setPurchasedCourseIds([]);
        setLicenseOwner(false);
    }, []);

    const value = useMemo(
        () => ({ user, purchasedCourseIds, licenseOwner, loading, login, register, logout, refresh }),
        [user, purchasedCourseIds, licenseOwner, loading, login, register, logout, refresh],
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
